import { ChangeEvent, ClipboardEvent, useEffect, useId, useRef, useState } from 'react'

import { useAddressSuggestions } from '@/hooks/use-address-suggestions'
import type { ApiAddressSuggestion } from '@/types/address-suggestions'
import { isAddressInDeliveryArea } from '@/lib/freight'
import { Input } from '@/app/[locale]/(ui)/input'
import { Frown, LoaderCircle, Smile } from 'lucide-react'
import { useClickOutside } from '@/hooks/use-click-outside'
import { useTranslations } from 'next-intl'
import { formatAddressSuggestion } from '@/lib/address-suggestions'

export const AddressInput = ({
  whenAddressIsSelected,
  preSelectedAddress,
  isValidAddress,
  setIsValidAddress = undefined,
  isB2bOrder = false,
  handleClearAddress = undefined,
  allowOutsideDeliveryArea = false,
  label,
  placeholder,
  required = false,
  error = '',
}: {
  address?: ApiAddressSuggestion
  preSelectedAddress?: ApiAddressSuggestion
  isValidAddress: boolean
  setIsValidAddress?: (isValid: boolean) => void
  allowOutsideDeliveryArea?: boolean
  isB2bOrder?: boolean
  handleClearAddress?: () => void
  whenAddressIsSelected: (address: ApiAddressSuggestion) => void
  label: string
  placeholder?: string
  required?: boolean
  error?: string
}) => {
  const id = useId()
  const t = useTranslations()
  const [input, setInput] = useState('')
  const [isAddressSelected, setIsAddressSelected] = useState(false)
  const [_isFocusedOut, setIsFocusedOut] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<ApiAddressSuggestion | null>(null)
  const address = useAddressSuggestions()
  const addressSuggestionsRef = useRef<HTMLDivElement>(null)
  // const hasError = isFocusedOut && !isAddressSelected && !isValidAddress

  // Check if address is only available for B2B (zone 5)
  const isAddressB2bOnly = (addr: ApiAddressSuggestion | null) => {
    if (!addr) return false
    const isValidForB2b = isAddressInDeliveryArea(addr.kommunenummer, addr.postnummer, true)
    const isValidForRegular = isAddressInDeliveryArea(addr.kommunenummer, addr.postnummer, false)
    return isValidForB2b && !isValidForRegular
  }

  const propagateAddressChange = ({
    newAddress,
    isSelected,
    isValid,
  }: {
    newAddress: string
    isSelected: boolean
    isValid: boolean
  }) => {
    setInput(newAddress)
    address.setValue(newAddress)
    setIsAddressSelected(isSelected)
    if (setIsValidAddress) setIsValidAddress(isValid)
  }

  // Logic to set the value of the selected address, empty the suggestions and emit the selected address
  const selectAddress = (selectedAddr: ApiAddressSuggestion) => {
    const isValidForCurrentOrder = isAddressInDeliveryArea(
      selectedAddr.kommunenummer,
      selectedAddr.postnummer,
      isB2bOrder,
    )

    setSelectedAddress(selectedAddr)
    propagateAddressChange({
      newAddress: formatAddressSuggestion(selectedAddr),
      isSelected: true,
      isValid: isValidForCurrentOrder,
    })
    whenAddressIsSelected(selectedAddr)
  }

  const handleInput = async (event: ChangeEvent<EventTarget>) => {
    const value = (event as ChangeEvent<HTMLInputElement>).target.value
    if (value === '') {
      handleClearAddressInput()
      return
    }
    propagateAddressChange({ newAddress: value, isSelected: false, isValid: false })
    showAddressSuggestions()
  }

  const handleClearAddressInput = () => {
    setSelectedAddress(null)
    propagateAddressChange({ newAddress: '', isSelected: false, isValid: false })
    hideAddressSuggestions()
    if (handleClearAddress) handleClearAddress()
  }

  const handlePaste = async (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    propagateAddressChange({ newAddress: e.clipboardData?.getData('text'), isSelected: false, isValid: false })
    showAddressSuggestions()
  }

  const handleFocusOut = () => {
    setIsFocusedOut(true)
    hideAddressSuggestions()
  }

  const hideAddressSuggestions = () => {
    if (addressSuggestionsRef?.current) addressSuggestionsRef.current.style.display = 'none'
  }

  const showAddressSuggestions = () => {
    if (addressSuggestionsRef?.current) addressSuggestionsRef.current.style.display = 'flex'
  }

  // If an address is already present, display that value
  useEffect(() => {
    if (preSelectedAddress) {
      selectAddress(preSelectedAddress)
    }
  }, [])

  const listRef = useClickOutside<HTMLDivElement>(handleFocusOut)

  return (
    <div ref={listRef} className="w-full">
      <div className="relative">
        <Input
          id={id}
          type="search"
          label={label}
          placeholder={placeholder}
          value={input}
          required={required}
          onChange={handleInput}
          onPaste={handlePaste}
          onFocus={showAddressSuggestions}
          error={error}
        />
      </div>
      <div ref={addressSuggestionsRef}>
        {(address.suggestions.length > 0 || address.isLoading) && !isAddressSelected && input && (
          <div className="absolute z-[2] mt-0 flex w-[400px] flex-col overflow-hidden rounded-[4px] border border-black bg-white">
            {/* Show loading indicator OR suggestions, not both */}
            {address.isLoading ? (
              <div className="box-border flex w-full items-center gap-2 px-4 pb-[13px] pt-4">
                <LoaderCircle className="animate-spin" />
                <span className="text-sm">{t('Loading')}</span>
              </div>
            ) : (
              address.suggestions?.slice(0, 5).map((suggestion, index) => {
                const { adressetekst, postnummer, poststed } = suggestion
                return (
                  <div
                    key={index}
                    className={`box-border w-full cursor-pointer px-4 py-[0.6em] text-sm hover:bg-black/10 ${
                      index === 0 ? 'border-t-0' : 'border-t border-black'
                    }`}
                    onClick={() => selectAddress(suggestion)}
                  >
                    {adressetekst}, {postnummer} {poststed}
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>
      {isAddressSelected && !allowOutsideDeliveryArea && (
        <div
          className={`mt-1 flex h-[48px] w-full items-center overflow-hidden rounded-[6px] border border-black px-3 py-2 ${
            isValidAddress ? 'bg-[#E3F8DC]' : 'bg-[#FEDED8]'
          }`}
        >
          {isValidAddress ? (
            <>
              <Smile size={20} className="mr-2" />
              <p className="text-sm">
                {isAddressB2bOnly(selectedAddress)
                  ? t('Valid_address_selected_in_zone_5')
                  : t('Valid_address_selected')}
              </p>
            </>
          ) : (
            <>
              <Frown size={20} className="mr-2" />
              <p className="text-sm">{t('Invalid_address_selected')}</p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
